import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faTrash,
  faPenToSquare,
} from "@fortawesome/free-solid-svg-icons";
import { useDispatch } from "react-redux";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { useAxiosInstance } from "../../../services/Interceptor";
import { toast } from "react-toastify";
import PremiumFeatureModal from "../../../components/PremiumFeatureModal";
import FreeTrialModal from "../../../components/FreeTrialModal";
import { StartFreeTrial } from "../../../services/PaymentService";
import { usePremiumUpsellRedirect } from "../../../hooks/usePremiumUpsellRedirect";
import { setProfileAction } from "../../../store/AthleteActions";
import { validateSubscriptionAction } from "../../../store/userActions";
import { bumpMenuRefresh } from "../../../store/slices/athleteSlice";

const statusLabel = {
  Committed: "Verbal commitment",
  Signed: "Signed letter of intent",
};

export default function OffersTab({
  offers,
  onOpenAdd,
  onDelete,
  hasAcceptedOffer,
  onAcceptanceChange,
  onRemoveAcceptance,
  savedAcceptanceData,
  onEditAcceptance,
  hasSubscriptionAccess = true,
  hasUsedFreeTrial = false,
}) {
  const authUser = useAuthUser();
  const { user = {} } = authUser || {};
  const dispatch = useDispatch();
  const apiInstance = useAxiosInstance();
  const [openPremiumFeatureModal, setOpenPremiumFeatureModal] = useState(false);
  const [openTrialModal, setOpenTrialModal] = useState(false);
  const showFreeTrial =
    !hasSubscriptionAccess && user?.role === "athlete" && !hasUsedFreeTrial;

  const openSubscription = usePremiumUpsellRedirect(user);

  const handlePremiumFreeTrial = () => {
    setOpenPremiumFeatureModal(false);
    setOpenTrialModal(true);
  };

  const handlePremiumSubscribe = () => {
    setOpenPremiumFeatureModal(false);
    openSubscription();
  };

  const handleOpenAdd = () => {
    if (!hasSubscriptionAccess) {
      setOpenPremiumFeatureModal(true);
      return;
    }
    onOpenAdd();
  };

  return (
    <div className="mt-4">
      <PremiumFeatureModal
        open={openPremiumFeatureModal}
        onClose={() => setOpenPremiumFeatureModal(false)}
        title="Premium Feature"
        description="Adding offers is locked. Start a free trial or subscribe now to update your profile."
        onStartFreeTrial={handlePremiumFreeTrial}
        onSubscribe={handlePremiumSubscribe}
        showStartFreeTrial={showFreeTrial}
      />
      <FreeTrialModal
        open={openTrialModal}
        onClose={() => setOpenTrialModal(false)}
        user={user}
        onStoreSuccessChain={async () => {
          const result = await StartFreeTrial(apiInstance, user.id);
          if (result.success) {
            toast.success("Free trial started!", { position: "top-right" });
            setOpenTrialModal(false);
            await dispatch(setProfileAction(apiInstance, user.id));
            await dispatch(
              validateSubscriptionAction(apiInstance, user.role, user.id),
            );
            dispatch(bumpMenuRefresh());
          } else {
            toast.error("Failed to start free trial", {
              position: "top-right",
            });
          }
        }}
      />
      <div
        className="float-right text-sm text-white border border-gray-300 p-2 rounded bg-blue-600 hover:bg-blue-800 cursor-pointer"
        onClick={handleOpenAdd}
      >
        <FontAwesomeIcon icon={faPlus} /> Add new Offers
      </div>

      <h2 className="font-medium mb-4 uppercase">Offers</h2>
      <div className="clear-both"></div>
      {Array.isArray(offers) && offers.length > 0 ? (
        <>
          <div className="border border-blue-700 rounded-md p-4 text-gray-800 mb-4 mt-2">
            <div className="grid grid-cols-4 gap-4 items-center max-h-[400px] overflow-y-auto">
              {offers.map((offer, index) => (
                <React.Fragment key={index}>
                  <div>{offer.schoolName}</div>
                  <div>{offer.division}</div>
                  <div className="justify-self-end font-bold">
                    {offer.offerYear}
                  </div>
                  <FontAwesomeIcon
                    icon={faTrash}
                    className="justify-self-end text-red-700 cursor-pointer"
                    onClick={() => onDelete(offer.id)}
                  />
                </React.Fragment>
              ))}
            </div>
          </div>
          <div className="mt-4">
            <p className="font-medium text-sm mb-2">
              Have you accepted any offers?
            </p>
            {hasAcceptedOffer === "yes" ? (
              <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-md px-4 py-3">
                <div className="text-sm text-gray-800">
                  <span className="font-semibold text-blue-700">
                    {statusLabel[savedAcceptanceData?.offerAcceptanceStatus] ??
                      savedAcceptanceData?.offerAcceptanceStatus}
                  </span>
                  {savedAcceptanceData?.offerAcceptanceSchool && (
                    <span className="ml-2 text-gray-500">
                      — {savedAcceptanceData.offerAcceptanceSchool}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-4 ml-4 shrink-0">
                  <button
                    type="button"
                    onClick={onEditAcceptance}
                    className="flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800"
                  >
                    <FontAwesomeIcon icon={faPenToSquare} />
                    Edit
                  </button>
                  <button
                    type="button"
                    onClick={onRemoveAcceptance}
                    className="text-sm text-gray-400 hover:text-red-600"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ) : (
              <div className="flex gap-6">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="hasAcceptedOffer"
                    value="yes"
                    checked={hasAcceptedOffer === "yes"}
                    onChange={() => onAcceptanceChange("yes")}
                  />
                  <span>Yes</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="hasAcceptedOffer"
                    value="no"
                    checked={hasAcceptedOffer === "no"}
                    onChange={() => onAcceptanceChange("no")}
                  />
                  <span>No</span>
                </label>
              </div>
            )}
          </div>
        </>
      ) : (
        <div
          onClick={handleOpenAdd}
          className="hover:underline text-blue-700 cursor-pointer"
        >
          Add your first offer here
        </div>
      )}
    </div>
  );
}
